import { useCallback } from 'react'
import { BoletoInsideConfigs, ConfigModel, FinancialConfigs, GeneralConfigs, InvoiceConfigs, ServiceOrderConfigs } from '@/domain/models'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { initialConfigsState, generalConfigsState, financialConfigsState, invoiceConfigsState, menuAccessPermissionsConfigsState, serviceOrderConfigsState, initialFinancialConfigsState, initialGeneralConfigsState, initialInvoiceConfigsState, inititalServiceOrderConfigsState, initialMenuAccessPermissionsConfigsState, hasErrorGeneralConfigState } from '@/presentation/pages/Settings/components'
import useNotify from './use-notify'

type SettingsProps = {
  startConfig: (config: ConfigModel) => void
  discardChanges: () => void
  isValidMail: (mail: string) => boolean
  validateSacOrigins: (config: ConfigModel) => boolean
}

export const useSettings = (): SettingsProps => {
  const notify = useNotify()
  const setGeneralErrors = useSetRecoilState(hasErrorGeneralConfigState)
  const [initialConfigs, setInitialConfigs] = useRecoilState(initialConfigsState)
  const setFinancialConfigs = useSetRecoilState(financialConfigsState)
  const setInititalFinancialConfigs = useSetRecoilState(initialFinancialConfigsState)
  const setGeneralConfigs = useSetRecoilState(generalConfigsState)
  const setInitialGeneralConfigs = useSetRecoilState(initialGeneralConfigsState)
  const setInvoiceConfigs = useSetRecoilState(invoiceConfigsState)
  const setInititalInvoiceConfigs = useSetRecoilState(initialInvoiceConfigsState)
  const setServiceOrderConfigs = useSetRecoilState(serviceOrderConfigsState)
  const setInitialServiceOrderConfigs = useSetRecoilState(inititalServiceOrderConfigsState)
  const setMenuAccessPermissionsConfigs = useSetRecoilState(menuAccessPermissionsConfigsState)
  const setInitialMenuAccessPermissionsConfigs = useSetRecoilState(initialMenuAccessPermissionsConfigsState)

  const discardChanges = useCallback((): void => {
    startConfig(initialConfigs)
  }, [initialConfigs])

  const isValidMail = useCallback((mail: string): boolean => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(mail)
  }, [])

  const validateSacOrigins = (config: ConfigModel): boolean => {
    if (Array.isArray(config.sacOrigensVisiveis) && !config.sacOrigensVisiveis.length) {
      setGeneralErrors(['sacOrigensVisiveis'])
      notify.error('Selecione ao menos uma origem de SAC.')
      return false
    }

    return true
  }

  const startConfig = (config: ConfigModel): void => {
    setInitialConfigs(config)
    setGeneralErrors([])

    const inicialFinancialConfigs: FinancialConfigs & BoletoInsideConfigs = {
      assuntoEmailConfirmacaoPgto: config.assuntoEmailConfirmacaoPgto,
      atualizaAutomaticoCartao: config.atualizaAutomaticoCartao,
      bolAtualizaBoletos: config.bolAtualizaBoletos,
      bolEntradaConfirmada: config.bolEntradaConfirmada,
      bolExibeOriginal: config.bolExibeOriginal,
      bolOcultaFormasPgto: config.bolOcultaFormasPgto,
      emailAlteracaoCartao: config.emailAlteracaoCartao,
      exibeAlteracaoCartao: config.exibeAlteracaoCartao,
      formasPagamento: config.formasPagamento,
      formasPagamentoAtualiza: config.formasPagamentoAtualiza,
      formasPagamentoNaoAtualiza: config.formasPagamentoNaoAtualiza,
      formasPagamentoOcultas: config.formasPagamentoOcultas,
      mensagemEmailConfirmacaoPgto: config.mensagemEmailConfirmacaoPgto,
      mensagemSMSConfirmacaoPgto: config.mensagemSMSConfirmacaoPgto,
      pagamentoCartaoAtivo: config.pagamentoCartaoAtivo,
      permitePagamentoCartao: config.permitePagamentoCartao,
      validaCartaoCredito: config.validaCartaoCredito,
      visualizaFaturaCartaoVencida: config.visualizaFaturaCartaoVencida,
      ocultaBoletosAssessoria: config.ocultaBoletosAssessoria
    }
    setFinancialConfigs(inicialFinancialConfigs)
    setInititalFinancialConfigs(inicialFinancialConfigs)

    const inicialServiceOrderConfigs: GeneralConfigs = {
      uniCodigo: config.uniCodigo,
      uniUnidade: config.uniUnidade,
      nome: config.nome,
      email: config.email,
      copia: config.copia,
      enviaEmailViagem: config.enviaEmailViagem,
      emailViagem: config.emailViagem,
      emailAlteracaoCadastro: config.emailAlteracaoCadastro,
      emailSac: config.emailSac,
      sacOrigensDisponiveis: config.sacOrigensDisponiveis,
      sacOrigensVisiveis: config.sacOrigensVisiveis,
      chaveResidencia: config.chaveResidencia,
      exibeChavesAvisoViagem: config.exibeChavesAvisoViagem,
      exibeAlteracaoCadastro: config.exibeAlteracaoCadastro,
      conPorCodigo: config.conPorCodigo,
      servidorSmtp: config.servidorSmtp,
      autentica: config.autentica,
      usuario: config.usuario,
      senha: config.senha,
      porta: config.porta,
      ssl: config.ssl,
      leitura: config.leitura,
      liberacoesPortal: config.liberacoesPortal,
      visualizaDetalhesContrato: config.visualizaDetalhesContrato,
      limiteDiasVisualizaCritica: config.limiteDiasVisualizaCritica,
      bloqueiaInativos: config.bloqueiaInativos,
      limiteDataRetroativa: config.limiteDataRetroativa
    }
    setGeneralConfigs(inicialServiceOrderConfigs)
    setInitialGeneralConfigs(inicialServiceOrderConfigs)

    const inicialInvoiceConfigs: InvoiceConfigs = {
      nfsOcultarInfos: config.nfsOcultarInfos,
      nfsOcultarLink: config.nfsOcultarLink
    }
    setInvoiceConfigs(inicialInvoiceConfigs)
    setInititalInvoiceConfigs(inicialInvoiceConfigs)

    const inicialMenuAccessPermissionsConfigs: ServiceOrderConfigs = {
      exibirOsFechadas: config.exibirOsFechadas,
      osSemTecnico: config.osSemTecnico,
      limitaOs: config.limitaOs,
      abrirOsSemFaturamento: config.abrirOsSemFaturamento,
      exibeMensagemAbrirOs: config.exibeMensagemAbrirOs,
      mensagemAberturaOs: config.mensagemAberturaOs,
      empresaPadraoOS: config.empresaPadraoOS,
      enviarAvisoAberturaOs: config.enviarAvisoAberturaOs,
      emailOs: config.emailOs,
      enviaListaChave: config.enviaListaChave,
      osEmpresaPadrao: config.osEmpresaPadrao,
      exibeAvisoAberturaOs: config.exibeAvisoAberturaOs,
      avisoAberturaOs: config.avisoAberturaOs
    }
    setServiceOrderConfigs(inicialMenuAccessPermissionsConfigs)
    setInitialServiceOrderConfigs(inicialMenuAccessPermissionsConfigs)

    setMenuAccessPermissionsConfigs(config.liberacoesPortal)
    setInitialMenuAccessPermissionsConfigs(config.liberacoesPortal)
  }

  return {
    startConfig,
    discardChanges,
    validateSacOrigins,
    isValidMail
  }
}
